/*
 * File: cell-action-more.ts
 * Created: 2025-12-06 11:22:06
 * Author: Val Liu <valuis0429@gmail.com>
 * -----
 * Last Modified: 2025-12-06 20:32:52
 * Modified By: Val Liu <valuis0429@gmail.com>
 */

import { Component, Prop } from "vue-property-decorator";
import CellActionBase from "../../base/cell-action-base";
import CellAction from "../../cell-action";

let activeMoreInstance: CellActionMore | null = null;

@Component
export default class CellActionMore extends CellActionBase {
    isOpen: boolean = false;

    // 讓 position: fixed 的 menu 有地方綁定定位
    menuStyle: Record<string, string> = {};

    // 延遲關閉計時器
    private closeTimer: number | null = null;
    private readonly closeDelay = 120; // 80~150 你可微調

    // 定位參數
    private readonly gap = 4;
    private readonly viewportPadding = 12;

    // 用箭頭函式確保 addEventListener 綁定時 this 不會掉
    updateMenuPosition() {
        if (!this.isOpen) return;

        this.$nextTick(() => {
            const trigger = resolveRefEl(this.$refs.triggerEl);
            const menu = resolveRefEl(this.$refs.dropdownMenu);

            if (!trigger || !menu) return;

            const rect = trigger.getBoundingClientRect();
            const gap = this.gap;
            const pad = this.viewportPadding;

            // 預設開啟策略：
            // - 一般：bottom-start
            // - 巢狀：right-start
            let top = this.inDropdown ? rect.top : rect.bottom + gap;
            let left = this.inDropdown ? rect.right + gap : rect.left;

            const mw = menu.offsetWidth;
            const mh = menu.offsetHeight;

            const vw = window.innerWidth;
            const vh = window.innerHeight;

            // ------- 水平修正 -------
            if (this.inDropdown) {
                // 右側不夠就翻到左側
                if (left + mw > vw - pad) {
                    const altLeft = rect.left - mw - gap;
                    if (altLeft >= pad) left = altLeft;
                    else left = Math.max(pad, vw - mw - pad);
                }
            } else {
                // 一般模式：如果右側不夠，改成 bottom-end（對齊 trigger 右邊）
                if (left + mw > vw - pad) {
                    const altLeft = rect.right - mw;
                    if (altLeft >= pad) left = altLeft;
                    else left = Math.max(pad, vw - mw - pad);
                }
            }

            if (left < pad) left = pad;

            // ------- 垂直修正 -------
            if (!this.inDropdown) {
                // 一般模式：下方不夠就往上開
                if (top + mh > vh - pad) {
                    const altTop = rect.top - mh - gap;
                    if (altTop >= pad) top = altTop;
                    else top = Math.max(pad, vh - mh - pad);
                }
            } else {
                // 巢狀：以 top-start 為主；若超出下方，讓 menu 底部對齊 trigger 底部
                if (top + mh > vh - pad) {
                    const altTop = rect.bottom - mh;
                    if (altTop >= pad) top = altTop;
                    else top = Math.max(pad, vh - mh - pad);
                }
            }

            if (top < pad) top = pad;

            this.menuStyle = {
                top: `${Math.round(top)}px`,
                left: `${Math.round(left)}px`,
            };
        });
    }

    // --- 核心互動邏輯 ---
    toggleDropdown() {
        if (this.isOpen) {
            // 如果已經開啟，再次點擊則關閉 (或是由 MouseLeave 處理)
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    }

    openDropdown() {
        // 如果目前有別的選單開著，且那個選單不是我，就把它關掉
        if (activeMoreInstance && activeMoreInstance !== this) {
            activeMoreInstance.closeDropdown();
        }

        // 把自己設為當前活動的實例
        activeMoreInstance = this;

        this.isOpen = true;
        // 開啟時立即計算位置
        this.updateMenuPosition();

        // 加入全域監聽以處理 Scroll/Resize 時的跟隨
        window.addEventListener("scroll", this.updateMenuPosition, true);
        window.addEventListener("resize", this.updateMenuPosition);
    }

    closeDropdown() {
        // 如果我是當前活動的那個，就把變數清空
        if (activeMoreInstance === this) {
            activeMoreInstance = null;
        }

        this.isOpen = false;
        window.removeEventListener("scroll", this.updateMenuPosition, true);
        window.removeEventListener("resize", this.updateMenuPosition, true);
        this.cancelAutoClose();
    }

    // --- 滑鼠感應邏輯 (Mouse Leave to Close) ---

    onMouseEnter() {
        // 滑鼠移入 (按鈕或選單)，取消關閉計時
        if (this.closeTimer !== null) {
            clearTimeout(this.closeTimer);
            this.closeTimer = null;
        }
    }

    onMouseLeave() {
        // 滑鼠移出，啟動延遲關閉
        // 給予 200ms 緩衝，避免滑鼠經過間隙時閃爍
        this.closeTimer = window.setTimeout(() => {
            this.closeDropdown();
        }, 200);
    }

    beforeDestroy() {
        this.closeDropdown();
        if (this.closeTimer) clearTimeout(this.closeTimer);
    }

    cancelAutoClose() {
        if (this.closeTimer) {
            window.clearTimeout(this.closeTimer);
            this.closeTimer = null;
        }
    }

    scheduleAutoClose() {
        this.cancelAutoClose();
        if (!this.isOpen) return;

        this.closeTimer = window.setTimeout(() => {
            this.closeDropdown();
        }, this.closeDelay);
    }

    get defaultVariant() {
        return CellAction.defaultVariant;
    }
}

function resolveRefEl(ref: any): HTMLElement | undefined {
    const r = Array.isArray(ref) ? ref[0] : ref;
    if (!r) return;

    // 1) 已經是 DOM
    if (r instanceof HTMLElement) return r;

    // 2) 是 Vue 元件實例
    const vm = r as Vue;
    const el = (vm as any).$el;
    if (el instanceof HTMLElement) return el;

    return;
}
